/**
 * What every tool file needs: one place to dispatch a command with `why` and `expected_revision`,
 * and the bounded payloads the reads return (plan Decisions 12, 13, 18).
 */
import type { Note, SongDocument, Take } from '../../song/types.ts';
import type { CommandResult } from '../bus.ts';
import { OUTPUT_BUDGET, ToolError } from '../envelope.ts';
import type { ToolContext } from '../types.ts';

export interface WriteFields {
  why: string;
  expected_revision?: number | undefined;
}

/** Notes returned in one take payload before the rest are summarised away. */
export const TAKE_NOTE_LIMIT = 24;

/** Notes a bounded take read never drops, however little of the budget the context leaves. */
export const TAKE_NOTE_FLOOR = 4;

export interface TakeData {
  take_id: string;
  source: Take['source'];
  notes: { p: number; s: number; d: number; v: number }[];
  notes_total: number;
  duration_s: number;
  voiced_ratio: number;
  median_clarity: number;
  pitch_range: [number, number];
  tempo_hint: number | null;
  audio_alignment?: {
    method: 'worklet-clock-and-browser-latency';
    input_latency_ms: number;
    output_latency_ms: number;
    capture_offset_s: number;
    compensation_s: number;
  };
  refining_job_id?: string;
  context?: TakeContext;
}

export interface TakeContext {
  key: string;
  target_bars: [number, number];
  target_track_id?: string;
  sections: { name: string; bar_from: number; bar_to: number }[];
  chords: { bar: number; symbol: string }[];
  other_tracks: {
    track_id: string;
    kind: SongDocument['tracks'][number]['kind'];
    notes_total: number;
    pitch_range?: [number, number];
  }[];
  /** True when the output budget forced evidence out of the lists above. */
  bounded?: true;
}

/**
 * Sends one command to the shared bus as the agent, carrying its reason.
 *
 * @param context - The tool context.
 * @param type - The command type lane A's reducer knows.
 * @param args - The command arguments.
 * @param fields - `why` and the optional `expected_revision` from the tool input.
 * @returns The reducer's result.
 */
export function dispatch(
  context: ToolContext,
  type: string,
  args: Record<string, unknown>,
  fields: WriteFields,
): CommandResult {
  return context.bus.dispatch({
    type,
    args,
    source: 'agent',
    why: fields.why,
    ...(fields.expected_revision === undefined
      ? {}
      : { expected_revision: fields.expected_revision }),
  });
}

/**
 * Adds `target_bars` to a tool payload when the reducer reported them.
 *
 * @param result - The reducer's result.
 * @returns An object to spread into the envelope's data.
 */
export function targetBars(result: CommandResult): { target_bars?: [number, number] } {
  return result.target_bars === undefined ? {} : { target_bars: result.target_bars };
}

/**
 * Finds a take or refuses with `TAKE_NOT_FOUND`.
 *
 * @param song - The current song.
 * @param takeId - The take id from the tool input.
 * @returns The take.
 */
export function requireTake(song: SongDocument, takeId: string): Take {
  const take = song.takes.find(({ id }) => id === takeId);
  if (!take) {
    throw new ToolError(
      'TAKE_NOT_FOUND',
      `Take "${takeId}" does not exist. Read get_song_state for the takes waiting.`,
      true,
    );
  }
  return take;
}

/**
 * Bounds a take for the model: the first notes, the counts and the quality readings.
 *
 * @param take - The take to describe.
 * @param beatsPerBar - The time signature numerator, for bar-relative starts.
 * @returns The payload `get_take` and `stop_recording` return.
 */
export function takeData(take: Take, beatsPerBar = 4): TakeData {
  const first = take.notes[0]?.s ?? 0;
  const offset = Math.floor(first / beatsPerBar) * beatsPerBar;
  const data: TakeData = {
    take_id: take.id,
    source: take.source,
    notes: take.notes.slice(0, TAKE_NOTE_LIMIT).map((note) => noteView(note, offset)),
    notes_total: take.notes.length,
    duration_s: round(take.duration_s),
    voiced_ratio: round(take.voiced_ratio),
    median_clarity: round(take.median_clarity),
    pitch_range: [Math.round(take.pitch_range[0]), Math.round(take.pitch_range[1])],
    tempo_hint: take.tempo_hint === null ? null : Math.round(take.tempo_hint),
  };
  if (take.refining_job_id !== undefined) {
    data.refining_job_id = take.refining_job_id;
  }
  if (take.audio?.alignment !== undefined) {
    data.audio_alignment = {
      method: take.audio.alignment.method,
      input_latency_ms: round(take.audio.alignment.input_latency_s * 1000),
      output_latency_ms: round(
        (take.audio.alignment.base_latency_s + take.audio.alignment.output_latency_s) * 1000,
      ),
      capture_offset_s: round(take.audio.alignment.capture_offset_s),
      compensation_s: round(take.audio.alignment.compensation_s),
    };
  }
  return data;
}

/** Returns the bounded song evidence around a rough take for musical interpretation. */
export function takeContext(song: SongDocument, take: Take): TakeContext {
  const beatsPerBar = song.time_sig[0];
  const performedBars =
    take.notes.length === 0 && take.target_bars !== undefined
      ? take.target_bars
      : takeBarRange(take, beatsPerBar, song.bars);
  const targetBars: [number, number] =
    take.target_bars === undefined
      ? performedBars
      : [
          Math.min(take.target_bars[0], performedBars[0]),
          Math.max(take.target_bars[1], performedBars[1]),
        ];
  const start = (targetBars[0] - 1) * beatsPerBar;
  const end = targetBars[1] * beatsPerBar;
  const otherTracks = song.tracks
    .filter(({ id }) => id !== take.target_track_id)
    .map((track) => {
      const notes = track.notes.filter((note) => note.s < end && note.s + note.d > start);
      const pitches = notes.map(({ p }) => p);
      return {
        track_id: track.id,
        kind: track.kind,
        notes_total: notes.length,
        ...(track.kind === 'drums' || pitches.length === 0
          ? {}
          : { pitch_range: [Math.min(...pitches), Math.max(...pitches)] as [number, number] }),
      };
    });
  return {
    key: song.key.name,
    target_bars: targetBars,
    ...(take.target_track_id === undefined ? {} : { target_track_id: take.target_track_id }),
    sections: song.sections.filter(
      (section) => section.bar_from <= targetBars[1] && section.bar_to >= targetBars[0],
    ),
    chords: song.chords.filter(({ bar }) => bar >= targetBars[0] && bar <= targetBars[1]),
    other_tracks: otherTracks,
  };
}

function takeBarRange(take: Take, beatsPerBar: number, fallbackBar: number): [number, number] {
  if (take.notes.length === 0) return [1, fallbackBar];
  const first = Math.min(...take.notes.map(({ s }) => s));
  const last = Math.max(...take.notes.map(({ s, d }) => Math.max(s, s + d - 0.000_001)));
  return [Math.floor(first / beatsPerBar) + 1, Math.floor(last / beatsPerBar) + 1];
}

/**
 * The order a take read gives ground in when the whole payload will not fit Chrome's budget: the
 * musical context is the addition, so it yields before the notes and quality readings that
 * `get_take` returned before the context existed. `notes_total` keeps every trim visible.
 */
const REDUCTIONS: ((data: TakeData) => TakeData | null)[] = [
  (data) =>
    shrinkContext(data, (context) =>
      context.other_tracks.length === 0
        ? null
        : { ...context, other_tracks: context.other_tracks.slice(0, -1), bounded: true },
    ),
  (data) =>
    shrinkContext(data, (context) =>
      context.sections.length === 0
        ? null
        : { ...context, sections: context.sections.slice(0, -1), bounded: true },
    ),
  (data) =>
    shrinkContext(data, (context) =>
      context.chords.length === 0
        ? null
        : { ...context, chords: context.chords.slice(0, -1), bounded: true },
    ),
  (data) =>
    data.notes.length <= TAKE_NOTE_FLOOR ? null : { ...data, notes: data.notes.slice(0, -1) },
];

function shrinkContext(
  data: TakeData,
  shrink: (context: TakeContext) => TakeContext | null,
): TakeData | null {
  if (data.context === undefined) return null;
  const context = shrink(data.context);
  return context === null ? null : { ...data, context };
}

/**
 * Trims a take read until the envelope it will sit in fits the output budget.
 *
 * @param data - The full payload, context included.
 * @param measure - Serialised length of the envelope built from a candidate payload.
 * @param budget - The character limit; defaults to Chrome's 1,500.
 * @returns The largest payload that fits, or the smallest reachable one.
 */
export function boundTakeRead(
  data: TakeData,
  measure: (candidate: TakeData) => number,
  budget = OUTPUT_BUDGET,
): TakeData {
  let current = data;
  for (const reduce of REDUCTIONS) {
    while (measure(current) > budget) {
      const next = reduce(current);
      if (next === null) break;
      current = next;
    }
    if (measure(current) <= budget) return current;
  }
  return current;
}

function noteView(note: Note, offset: number): { p: number; s: number; d: number; v: number } {
  return { p: note.p, s: round(note.s - offset), d: round(note.d), v: round(note.v) };
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
